package world.phantasmal.psolib.compatibility

/**
 * Result of a compatibility check for a specific PSO version.
 */
data class CompatibilityResult(
    val version: PSOVersion,
    val errors: List<CompatibilityProblem> = emptyList(),
    val warnings: List<CompatibilityProblem> = emptyList(),
) {
    val isFullyCompatible: Boolean
        get() = errors.isEmpty() && warnings.isEmpty()

    val hasErrors: Boolean
        get() = errors.isNotEmpty()

    val hasWarnings: Boolean
        get() = warnings.isNotEmpty()
}

/**
 * A compatibility problem (error or warning).
 */
data class CompatibilityProblem(
    val type: ProblemType,
    val message: String,
    val location: ProblemLocation? = null,
)

/**
 * Type of compatibility problem.
 */
enum class ProblemType {
    // Script problems
    MISSING_LABEL_0,
    UNKNOWN_OPCODE,
    OPCODE_VERSION_MISMATCH,
    EPISODE_NOT_SUPPORTED,
    INVALID_ARGUMENT,
    LABEL_NOT_FOUND,
    // Reserved for future checks.
    SWITCH_ARRAY_MISMATCH,
    CONVERSION_WARNING,
    SPECIAL_OPCODE_WARNING,

    // NPC/Monster problems
    NPC_ACTION_LABEL_NOT_FOUND,
    // Reserved for future checks.
    NPC_SCRIPT_LABEL_NOT_SUPPORTED,  // Custom script labels only work in BB (v4)
    SKIN_NOT_SUPPORTED,
    SKIN_51_INVALID_SUBTYPE,
    MONSTER_FLOOR_MISMATCH,
    TOO_MANY_MONSTERS,

    // Object problems
    OBJECT_FLOOR_MISMATCH,
    TOO_MANY_OBJECTS,
    BOSS_TELEPORTER_SOURCE_MISMATCH,
    BOSS_TELEPORTER_TARGET_NOT_BOSS,

    // Data problems
    UNUSED_DATA_LABEL,

    // File problems
    // Reserved for future checks.
    MISSING_BIN_FILE,
    // Reserved for future checks.
    MISSING_DAT_FILE,
    // Reserved for future checks.
    PVR_NOT_SUPPORTED,
}

/**
 * Location of a compatibility problem.
 */
sealed interface ProblemLocation {
    data class ScriptLine(val lineNumber: Int) : ProblemLocation
    data class Monster(val index: Int, val floorId: Int) : ProblemLocation
    data class Object(val index: Int, val floorId: Int) : ProblemLocation
    data class Floor(val floorId: Int) : ProblemLocation
    data class DataLabel(val label: Int) : ProblemLocation
}

/**
 * Builder for creating CompatibilityResult.
 */
internal class CompatibilityResultBuilder(private val version: PSOVersion) {
    private val errors = mutableListOf<CompatibilityProblem>()
    private val warnings = mutableListOf<CompatibilityProblem>()

    fun addError(type: ProblemType, message: String, location: ProblemLocation? = null) {
        errors.add(CompatibilityProblem(type, message, location))
    }

    fun addWarning(type: ProblemType, message: String, location: ProblemLocation? = null) {
        warnings.add(CompatibilityProblem(type, message, location))
    }

    fun build(): CompatibilityResult = CompatibilityResult(version, errors.toList(), warnings.toList())
}
